import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface Crl8Api {
  ready: (callback: () => void) => void;
  createExperience: (galleryName: string) => Promise<void>;
  destroyExperience: (galleryName: string) => void | Promise<void>;
}

declare global {
  interface Window {
    crl8?: Crl8Api;
  }
}

interface UseBazaarvoiceGalleryOptions {
  galleryName: string;
  autoInit?: boolean;
  containerRef?: RefObject<HTMLElement | null>;
}

export const useBazaarvoiceGallery = ({
  galleryName,
  autoInit = false,
  containerRef,
}: UseBazaarvoiceGalleryOptions) => {
  const [isReady, setIsReady] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const hasInitialized = useRef(false);
  const isCleaningUp = useRef(false);

  useEffect(() => {
    if (!window.crl8?.ready) {
      return;
    }

    window.crl8.ready(() => {
      setIsReady(true);

      if (autoInit && !hasInitialized.current && !isCleaningUp.current) {
        window.crl8?.createExperience(galleryName);
        setIsCreated(true);
        hasInitialized.current = true;
      }
    });

    if (autoInit) {
      return () => {
        isCleaningUp.current = true;
        if (window.crl8?.ready && hasInitialized.current) {
          window.crl8.ready(() => {
            const destroyPromise = window.crl8?.destroyExperience(galleryName);
            if (destroyPromise) {
              destroyPromise.catch(console.error);
            }
            setIsCreated(false);
          });
        }
      };
    }
  }, [galleryName, autoInit]);

  const createGallery = useCallback(() => {
    if (window.crl8?.ready && isReady && !hasInitialized.current) {
      window.crl8.ready(() => {
        const createPromise = window.crl8?.createExperience(galleryName);
        if (createPromise) {
          createPromise.catch(console.error);
        }
        setIsCreated(true);
        hasInitialized.current = true;
      });
    }
  }, [isReady, galleryName]);

  const destroyGallery = useCallback(() => {
    if (window.crl8?.ready && hasInitialized.current) {
      const container = containerRef?.current;
      if (container) {
        window.crl8.ready(() => {
          try {
            window.crl8?.destroyExperience(galleryName);
            setIsCreated(false);
          } catch (error) {
            console.warn('Failed to destroy Bazaarvoice gallery:', error);
          }
        });
      }
    }
  }, [galleryName, containerRef]);

  return {
    isReady,
    isCreated,
    createGallery,
    destroyGallery,
  };
};

export const useVibeGallery = (galleryName: string) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isReady, createGallery, destroyGallery } = useBazaarvoiceGallery({
    galleryName,
    autoInit: false,
    containerRef,
  });

  useLayoutEffect(() => {
    if (isReady) {
      createGallery();
      return destroyGallery;
    }
  }, [isReady, createGallery, destroyGallery]);

  return { containerRef, isReady };
};
